import { useEffect, useState } from "react";
import type { Business, Perk } from "@dropin/shared/schemas";
import {
  type ClaimRow,
  createPerk,
  listIncomingClaims,
  listMyPerks,
  updateClaim,
  updatePerk,
} from "../api";
import { BusinessProfileTab } from "./BusinessProfileTab";
import { Toggle } from "./Toggle";
import { PerkForm } from "./PerkForm";

type Tab = "profile" | "perks" | "claims";

export function BusinessDashboard({
  business,
  onBusinessUpdated,
}: {
  business: Business;
  onBusinessUpdated: (b: Business) => void;
}) {
  const isPro = business.plan === "pro";
  const [tab, setTab] = useState<Tab>("profile");
  const [perks, setPerks] = useState<Perk[]>([]);
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    if (!isPro) {
      setLoading(false);
      return;
    }
    const [p, c] = await Promise.all([listMyPerks(), listIncomingClaims()]);
    setPerks(p.perks);
    setClaims(c.claims);
    setLoading(false);
  }

  useEffect(() => {
    if (isPro && (tab === "perks" || tab === "claims")) refresh();
  }, [tab, isPro]);

  const tabs = isPro
    ? [
        { key: "profile" as const, label: "Profile", testid: "tab-profile" },
        { key: "perks" as const, label: `Perks (${perks.length})`, testid: "tab-perks" },
        { key: "claims" as const, label: `Claims (${claims.length})`, testid: "tab-claims" },
      ]
    : [
        { key: "profile" as const, label: "Profile", testid: "tab-profile" },
      ];

  return (
    <>

      {!isPro && <FreePlanBanner businessId={business.id} />}

      <SegmentedTabs tab={tab} setTab={setTab} items={tabs} />

      {tab === "profile" && (
        <BusinessProfileTab business={business} onUpdated={onBusinessUpdated} />
      )}
      {tab === "perks" && isPro && (
        <PerksTab
          perks={perks}
          loading={loading}
          onChanged={refresh}
          plan={business.plan}
          businessId={business.id}
        />
      )}
      {tab === "claims" && isPro && (
        <ClaimsTab claims={claims} loading={loading} onChanged={refresh} />
      )}
    </>
  );
}

function FreePlanBanner({ businessId }: { businessId: string }) {
  return (
    <div
      className="rounded-lg border border-primary/30 bg-primary/[0.04] p-4 flex items-start gap-3 flex-wrap"
      data-testid="free-plan-banner"
    >
      <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">Free plan: brief-only</p>
        <p className="mt-0.5 text-sm text-muted leading-relaxed">
          Publish a creator brief and share it with creators in your neighborhood.
          Posting perks and the claims inbox unlock with Pro.
        </p>
      </div>
      <a
        href="https://getdropins.com/pricing"
        target="_blank"
        rel="noreferrer"
        className="btn-primary text-xs"
        data-testid="banner-upgrade"
      >
        Upgrade to Pro
      </a>
    </div>
  );
}

function SegmentedTabs<T extends string>({
  tab,
  setTab,
  items,
}: {
  tab: T;
  setTab: (t: T) => void;
  items: Array<{ key: T; label: string; testid: string }>;
}) {
  return (
    <div className="inline-flex rounded-md border border-border bg-paper p-1">
      {items.map((it) => (
        <button
          key={it.key}
          onClick={() => setTab(it.key)}
          data-testid={it.testid}
          className={`rounded px-3.5 py-1.5 text-sm font-semibold transition-colors ${
            tab === it.key
              ? "bg-surface text-ink shadow-xs"
              : "text-muted hover:text-ink"
          }`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

function PerksTab({
  perks,
  loading,
  onChanged,
  plan,
  businessId,
}: {
  perks: Perk[];
  loading: boolean;
  onChanged: () => void;
  plan?: "free" | "pro";
  businessId: string;
}) {
  const hasActiveOnFree = plan === "free" && perks.some((p) => p.active);

  return (
    <>
      <div className="card">
        <h3 className="text-lg font-semibold tracking-tightish">Post a new perk</h3>
        {hasActiveOnFree && (
          <p className="mt-2 text-sm text-muted">
            You're on the Free plan with one active perk. Deactivate it below, or{" "}
            <a href="https://getdropins.com/pricing" target="_blank" rel="noreferrer">
              upgrade to Pro
            </a>{" "}
            for unlimited.
          </p>
        )}
        <div className="mt-4">
          <PerkForm
            businessId={businessId}
            testidPrefix="create-perk"
            onSubmit={async (values) => {
              await createPerk({
                title: values.title,
                description: values.description,
                imageUrl: values.imageUrl ?? undefined,
                redemptionCode: values.redemptionCode ?? undefined,
                requireApproval: values.requireApproval,
              });
              await onChanged();
            }}
          />
        </div>
      </div>

      <div className="grid gap-3">
        {loading && <div className="card text-muted">Loading…</div>}
        {!loading && perks.length === 0 && (
          <div className="card text-muted">No perks yet — post your first above.</div>
        )}
        {perks.map((p) => (
          <PerkCard key={p.id} perk={p} businessId={businessId} onChanged={onChanged} />
        ))}
      </div>
    </>
  );
}

function PerkCard({
  perk,
  businessId,
  onChanged,
}: {
  perk: Perk;
  businessId: string;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="card card-hover" data-testid={`perk-card-${perk.id}`}>
      {editing ? (
        <PerkForm
          businessId={businessId}
          initial={perk}
          testidPrefix={`perk-edit-${perk.id}`}
          onCancel={() => setEditing(false)}
          onSubmit={async (values) => {
            await updatePerk(perk.id, values);
            setEditing(false);
            await onChanged();
          }}
        />
      ) : (
        <PerkCardView
          perk={perk}
          onEdit={() => setEditing(true)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

function PerkCardView({
  perk,
  onEdit,
  onChanged,
}: {
  perk: Perk;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const p = perk;
  return (
    <div className="flex items-start gap-3">
      {p.imageUrl && (
        <div className="shrink-0 h-16 w-16 rounded-md border border-border bg-paper overflow-hidden">
          <img
            src={p.imageUrl}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="text-base font-semibold">{p.title}</h4>
          {p.requireApproval && (
            <span className="chip-accent text-xs">Approval required</span>
          )}
        </div>
        <p className="mt-1 text-sm text-muted">{p.description || "—"}</p>
        {p.redemptionCode && (
          <p className="mt-2 text-xs text-muted">
            Code:{" "}
            <span className="font-mono uppercase tracking-wider text-ink bg-paper px-1.5 py-0.5 rounded border border-border">
              {p.redemptionCode}
            </span>
          </p>
        )}
        <div className="mt-3 flex items-center gap-3 flex-wrap text-xs text-muted">
          <div className="flex items-center gap-2">
            <span>Require approval</span>
            <Toggle
              active={p.requireApproval}
              onChange={async (next) => {
                await updatePerk(p.id, { requireApproval: next });
                onChanged();
              }}
              testid={`perk-require-approval-${p.id}`}
            />
          </div>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={onEdit}
            data-testid={`perk-edit-${p.id}`}
          >
            Edit
          </button>
        </div>
      </div>
      <Toggle
        active={p.active}
        onChange={async (next) => {
          await updatePerk(p.id, { active: next });
          onChanged();
        }}
        labelOn="Active"
        labelOff="Hidden"
        testid={`perk-toggle-${p.id}`}
      />
    </div>
  );
}


function ClaimsTab({
  claims,
  loading,
  onChanged,
}: {
  claims: ClaimRow[];
  loading: boolean;
  onChanged: () => void;
}) {
  if (loading) return <div className="card text-muted">Loading…</div>;
  if (claims.length === 0) {
    return <div className="card text-muted">No claims yet.</div>;
  }
  return (
    <div className="grid gap-3">
      {claims.map((c) => (
        <ClaimCard key={c.id} claim={c} onChanged={onChanged} />
      ))}
    </div>
  );
}

function ClaimCard({ claim, onChanged }: { claim: ClaimRow; onChanged: () => void }) {
  const c = claim;
  const name = c.creatorDisplayName || c.creatorEmail || "Creator";
  const initial = (name?.[0] ?? "?").toUpperCase();
  const locationLine = [c.creatorNeighborhood, c.creatorCity].filter(Boolean).join(" · ");
  return (
    <div className="card" data-testid={`claim-card-${c.id}`}>
      {/* Creator header */}
      <div className="flex items-start gap-3">
        <div className="shrink-0 h-11 w-11 rounded-full border border-border bg-paper overflow-hidden flex items-center justify-center">
          {c.creatorAvatarUrl ? (
            <img
              src={c.creatorAvatarUrl}
              alt=""
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <span className="text-base font-semibold text-muted" aria-hidden>
              {initial}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold text-ink truncate" data-testid={`claim-creator-name-${c.id}`}>
              {name}
            </span>
            {c.creatorVerified ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-success"
                title="Verified creator — vetted by a DropIn admin"
                data-testid={`creator-verified-${c.id}`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M20 6 9 17l-5-5" />
                </svg>
                Verified
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted"
                title="Unverified creator — has not been vetted yet"
                data-testid={`creator-unverified-${c.id}`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
                Unverified
              </span>
            )}
          </div>
          {locationLine && <p className="text-xs text-muted truncate">{locationLine}</p>}
          {c.creatorEmail && c.creatorDisplayName && (
            <p className="text-xs text-muted truncate">{c.creatorEmail}</p>
          )}
        </div>
        <ClaimStatusChip status={c.status} />
      </div>

      {c.creatorBio && (
        <p className="mt-3 text-sm text-muted leading-relaxed">{c.creatorBio}</p>
      )}

      {c.creatorSocialLinks && Object.keys(c.creatorSocialLinks).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {Object.entries(c.creatorSocialLinks).map(([platform, handle]) =>
            handle ? (
              <span
                key={platform}
                className="chip text-xs"
                data-testid={`claim-social-${platform}-${c.id}`}
              >
                {platform === "twitter" ? "X" : platform.charAt(0).toUpperCase() + platform.slice(1)}: {handle}
              </span>
            ) : null,
          )}
        </div>
      )}

      <div className="mt-4 border-t border-border pt-3 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-muted font-semibold">Claiming</p>
          <h4 className="mt-0.5 text-base font-semibold">{c.perkTitle}</h4>
          <ClaimContent claim={c} />
        </div>
        <div className="flex flex-wrap gap-2">
          {c.status === "pending" && (
            <>
              <button
                className="btn-primary text-xs"
                onClick={async () => {
                  await updateClaim(c.id, { status: "approved" });
                  onChanged();
                }}
                data-testid={`claim-approve-${c.id}`}
              >
                Approve
              </button>
              <button
                className="btn text-xs"
                onClick={async () => {
                  await updateClaim(c.id, { status: "rejected" });
                  onChanged();
                }}
                data-testid={`claim-reject-${c.id}`}
              >
                Reject
              </button>
            </>
          )}
          {c.status === "approved" &&
            !(c.contentLinks && c.contentLinks.length > 0) &&
            !c.contentUrl && (
              <span className="text-xs text-muted">Awaiting their post…</span>
            )}
        </div>
      </div>
    </div>
  );
}

// Renders the creator's content submission for a claim: prefers the
// new contentLinks array, falls back to the legacy single contentUrl.
function ClaimContent({ claim }: { claim: ClaimRow }) {
  const links =
    claim.contentLinks && claim.contentLinks.length > 0
      ? claim.contentLinks
      : claim.contentUrl
        ? [{ label: "Post", url: claim.contentUrl }]
        : [];
  if (links.length === 0 && !claim.note) return null;
  return (
    <div className="mt-2 grid gap-2">
      {links.length > 0 && (
        <ul className="grid gap-1">
          {links.map((l, i) => (
            <li key={i} className="flex items-baseline gap-2">
              <span className="text-xs text-muted shrink-0">{l.label}</span>
              <a
                href={l.url}
                target="_blank"
                rel="noreferrer"
                className="text-sm truncate"
                data-testid={`claim-link-${claim.id}-${i}`}
              >
                {l.url} ↗
              </a>
            </li>
          ))}
        </ul>
      )}
      {claim.note && (
        <p
          className="text-xs text-muted whitespace-pre-wrap leading-relaxed"
          data-testid={`claim-note-${claim.id}`}
        >
          <span className="font-semibold">Note:</span> {claim.note}
        </p>
      )}
    </div>
  );
}

function ClaimStatusChip({ status }: { status: string }) {
  const className =
    status === "approved" || status === "fulfilled"
      ? "chip-success"
      : status === "rejected"
        ? "chip"
        : "chip-accent";
  return <span className={className}>{status}</span>;
}
